import React from 'react';
import { FILE_TYPES } from 'constants/inputTypes';
import { scYellow100 } from 'constants/systemColors';
import { size } from 'lodash/collection';
import { isFunction } from 'lodash/lang';
import moment from 'moment';

import Checkbox from '../../../../../common/data-entry/InputSelectors/Checkbox';
import { PS_NOT_ASKED, PS_UNAVAILABLE } from '../../../patient-source/shared/EncounterDescNew/TableItemGroup/constants';

import CheckboxTemplate from './CheckboxTemplate';
import DateTemplate from './DateTemplate';
import DropdownTemplate from './DropdownTemplate';
import LabelTemplate from './LabelTemplate';
import MultipleFileTemplate from './MultipleFileTemplate';
import RadioTemplate from './RadioTemplate';
import ReminderTemplate from './ReminderTemplate';
import TextareaTemplate from './TextAreaTemplate';
import TextTemplate from './TextTemplate';
import TimeTemplate from './TimeTemplate';

const GenerateHtml = props => {
  const getElementByName = (data, type, props) => {
    switch (type) {
      case 'textbox':
        return (
          <TextTemplate
            isAbleToEdit={props.isAbleToEdit}
            templateData={data}
            onChangeTextHandler={props.onChangeTextHandler}
            formId={props.formId}
            formLabelId={props.formLabelId}
            itemId={props.itemId}
            customClassObject={props.customClassObject && props.customClassObject.textbox}
            isItemGroup
          />
        );
      case 'dropdown':
        return (
          <DropdownTemplate
            isAbleToEdit={props.isAbleToEdit}
            templateData={data}
            fullData={props.data}
            formId={props.formId}
            formLabelId={props.formLabelId}
            itemId={props.itemId}
            onChangeDropDownHandler={props.onChangeDropDownHandler}
            customClassObject={props.customClassObject && props.customClassObject.dropdown}
            groupAssignPermissionType={props.groupAssignPermissionType}
          />
        );
      case 'date':
        return (
          <DateTemplate
            disabled={isFunction(props.isAbleToEdit) && !props.isAbleToEdit()}
            value={data.inputValue}
            legacyLook
            placeholder={'date'}
            onChange={(e, formattedDate) => {
              let date = null;
              if (e && e._d) {
                date = moment(e._d);
                date.formattedDate = formattedDate;
              }
              isFunction(props.onDateChangeHandler) &&
                props.onDateChangeHandler(date, props.formLabelId, props.itemId, data);
            }}
          />
        );
      case 'radio':
        return (
          <RadioTemplate
            isAbleToEdit={props.isAbleToEdit}
            templateData={data}
            onChangeRadioHandler={props.onChangeRadioHandler}
            fullData={props.data}
            forPreview={props.forPreview}
          />
        );
      case 'textarea':
        return (
          <TextareaTemplate
            isAbleToEdit={props.isAbleToEdit}
            templateData={data}
            onChangeTextHandler={props.onChangeTextHandler}
            formId={props.formId}
            formLabelId={props.formLabelId}
            itemId={props.itemId}
            customClassObject={props.customClassObject && props.customClassObject.textarea}
            isItemGroup
          />
        );
      case 'time':
        return (
          <TimeTemplate
            isAbleToEdit={props.isAbleToEdit}
            templateData={data}
            onchangeTimeHandler={props.onchangeTimeHandler}
            formId={props.formId}
            formLabelId={props.formLabelId}
            itemId={props.itemId}
            customClassObject={props.customClassObject && props.customClassObject.time}
          />
        );
      case 'checkbox':
        return (
          <CheckboxTemplate
            disabled={props.disabled}
            templateData={data}
            onchangeCheckboxHandler={props.onchangeCheckboxHandler}
            fullData={props.data}
            formId={props.formId}
            formLabelId={props.formLabelId}
            itemId={props.itemId}
            customClassObject={props.customClassObject && props.customClassObject.checkbox}
            showInvestigatorFieldIcon={props.showInvestigatorFieldIcon}
            styleForItem={props.styleForItem}
          />
        );
      case 'label':
        return (
          <LabelTemplate
            templateData={data}
            formId={props.formId}
            formLabelId={props.formLabelId}
            itemId={props.itemId}
            onChangeLabelTextHandler={props.onChangeLabelTextHandler}
            customClassObject={props.customClassObject && props.customClassObject.label}
          />
        );
      case 'file':
        return (
          <MultipleFileTemplate
            templateData={data}
            formGroupIdentifier={props.formGroupIdentifier}
            onChangeFileHandler={props.onChangeFileHandler}
            deleteFile={props.deleteFile}
            isAbleToEditFunction={props.isAbleToEdit}
            onCloseSelectedFile={props.onCloseSelectedFile}
            isSomeCertifyCopyWasNotConfirmedAfterSaving={props.validation?.isSomeCertifyCopyWasNotConfirmedAfterSaving}
            isPatientItemGroupFile={props.isPatientItemGroupFile}
            filesToUploadGeneralSize={props.filesToUploadGeneralSize}
          />
        );
      case 'singleFile':
        return (
          <MultipleFileTemplate
            templateData={data}
            formGroupIdentifier={props.formGroupIdentifier}
            onChangeFileHandler={props.onChangeFileHandler}
            deleteFile={props.deleteFile}
            deleteFileForSingleFileUpload={props.deleteFileForSingleFileUpload}
            isAbleToEditFunction={props.isAbleToEdit}
            onCloseSelectedFile={props.onCloseSelectedFile}
            isSomeCertifyCopyWasNotConfirmedAfterSaving={props.validation?.isSomeCertifyCopyWasNotConfirmedAfterSaving}
            multiple={false}
            isPatientItemGroupFile={props.isPatientItemGroupFile}
            filesToUploadGeneralSize={props.filesToUploadGeneralSize}
          />
        );
      case 'reminder':
        return (
          <ReminderTemplate
            templateData={data}
            customClassObject={props.customClassObject && props.customClassObject.reminder}
            showInvestigatorFieldIcon={props.showInvestigatorFieldIcon}
            styleForItem={props.styleForItem}
          />
        );
      case 'multiselect':
        return (
          <DropdownTemplate
            isAbleToEdit={props.isAbleToEdit}
            templateData={data}
            fullData={props.data}
            formId={props.formId}
            formLabelId={props.formLabelId}
            itemId={props.itemId}
            onChangeDropDownHandler={props.onChangeDropDownHandler}
            customClassObject={props.customClassObject && props.customClassObject.multiselect}
            groupAssignPermissionType={props.groupAssignPermissionType}
          />
        );
      default:
        return '';
    }
  };

  const renderElement = (question, props) => {
    const eleList = [];
    if (questionHasAnswersToChoose(question) && questionHasCodeDefinitionList(question)) {
      eleList.push(
        getElementByName(
          question.codeDefinationList,
          question.codeDefinationList && question.codeDefinationList[0].inputType,
          props
        )
      );
    } else {
      eleList.push(getElementByName(question, question.inputType, props));
    }
    return eleList;
  };

  const questionHasCodeDefinitionList = question => {
    return question.hasOwnProperty('codeDefinationList') && Object.keys(question.codeDefinationList).length > 0;
  };

  const questionHasAnswersToChoose = question => {
    return (
      question.inputType === 'radio' ||
      question.inputType === 'dropdown' ||
      question.inputType === 'dropdownCT' ||
      question.inputType === 'multiselect' ||
      question.inputType === 'multiselectCT'
    );
  };

  const getElementType = ele => {
    return ele.inputType;
  };

  const getRenderElementClasses = (dt, page) => {
    if (dt.name === 'Notes' || dt.inputType === 'textarea' || dt.inputType === 'reminder') {
      return 'width50';
    }
    if (page && (page === 'encounterDetail' || page === 'popUp')) {
      // || page === 'popUp'
      if (dt.hasOwnProperty('measurementDefinitionList') && dt.measurementDefinitionList.length > 0) {
        return 'item-group-field-input';
      }
      if (FILE_TYPES.includes(dt.inputType)) {
        return 'item-group-field-input';
      }
    }
    if (dt.inputType === 'textBlockLong') {
      return '';
    }

    if (dt.inputType === 'checkbox') {
      return 'item-group-field-label';
    }

    return 'item-group-field-input';
  };

  const { data, fromWhichPage } = props;
  const type = getElementType(data);
  const listRl = renderElement(data, props);
  const className = getRenderElementClasses(data, fromWhichPage);
  const disabled = isFunction(props.isAbleToEdit) && !props.isAbleToEdit();

  return (
    <React.Fragment>
      <div className={className} style={{ display: 'inherit' }}>
        {listRl.map((v, idx) => {
          return (
            v && (
              <div key={idx} className={size(data.measurementDefinitionList) > 0 ? 'width50' : 'width100'}>
                {v}
              </div>
            )
          );
        })}
      </div>
      {type === 'checkbox' && <div className="item-group-field-input" />}
      {type !== 'textBlockLong' &&
        type !== 'label' &&
        type !== 'reminder' &&
        props.isRequireAttention &&
        props.onChangeRequireAttention &&
        props.highlightForReviewCheckboxNeeded && (
          <Checkbox
            color={scYellow100}
            className="pl-2"
            onChange={event => props.onChangeRequireAttention(event, props.data)}
            name="pay-attention"
            id={`pay-attention-${props.itemDefIndex}`}
            checked={!!props.isRequireAttention(props.data)}
            disabled={disabled}
          />
        )}
      {props.data.ansComments ? (
        <div className="pl-3 col-md-3 overflow-ellipsis-multiple-line font-italic" style={{ height: 'fit-content' }}>
          {props.data.ansStatus === PS_NOT_ASKED
            ? 'Not Asked: '
            : props.data.ansStatus === PS_UNAVAILABLE
            ? 'Unavailable: '
            : ''}
          {props.data.ansComments}
        </div>
      ) : (
        ''
      )}
    </React.Fragment>
  );
};

export default GenerateHtml;
